import {
  GetSiteIdForBoardDocument,
  GetSiteIdForBoardFragmentFragmentDoc,
  useHLPConfigForSiteQuery,
  useIHConfigQuery,
  useQASettingsQuery,
  useSQDCBoardGetNameQuery,
  useSQDCBoardsForHierarchyQuery,
  type GetSiteIdForBoardFragmentFragment,
  type GetSiteIdForBoardQuery,
} from "@/graphql";
import { apolloClient } from "@/graphql/apollo";
import apolloCache from "@/graphql/apolloCache";
import store from "@/store";
import { BoardIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import { asyncComputed } from "@vueuse/core";
import { gql } from "@apollo/client";
import {
  computed,
  inject,
  nextTick,
  ref,
  unref,
  type ComputedRef,
  type MaybeRef,
  type Ref,
} from "vue";

export function useIdeaConfig(siteId: Ref<number | undefined>) {
  const queryCondition = computed(() => (siteId?.value ?? 0) > 0);
  const { loading, result } = useHLPConfigForSiteQuery(
    () => {
      return {
        siteId: siteId.value!,
      };
    },
    () => ({
      fetchPolicy: "cache-first",
      enabled: queryCondition.value,
    }),
  );
  return {
    loadingIdeaConfig: loading,
    resultIdeaConfig: computed(() =>
      result.value == null ? undefined : (result.value?.HLPConfig ?? null),
    ),
  };
}

export function useIHConfig(siteIdLike: MaybeRef<number | undefined>) {
  const siteId = computed(() => unref(siteIdLike)); // unref to handle old computed values better use toRef instead of computed to throw parameter in

  const { result, loading } = useIHConfigQuery(
    computed(() => ({ siteId: siteId.value! })),
    computed(() => ({
      fetchPolicy: "cache-first",
      enabled: siteId.value != null,
    })),
  );

  return {
    loadingIHConfig: loading,
    resultIHConfig: computed(() => result.value?.GetIHConfigBySiteId ?? null),
  };
}

export function useQAConfig(siteId: Ref<number | undefined>) {
  const { result, loading } = useQASettingsQuery(
    computed(() => ({ siteId: siteId.value! })),
    computed(() => ({
      fetchPolicy: "cache-first",
      enabled: siteId.value != null,
    })),
  );

  return {
    loadingQYConfig: loading,
    resultQAConfig: computed(() => result.value?.QASettings?.[0] ?? null),
  };
}

export function useSpecifiedBoardContext(originBoardId: Ref<number | undefined>) {
  const boardLoading = ref(false);
  const board = asyncComputed(
    async () => {
      if (originBoardId.value == null) {
        return undefined;
      }

      const identifiedTp = apolloCache.identify({
        __typename: "SQDCBoard",
        id: originBoardId.value,
      });
      const fragment = apolloCache.readFragment<GetSiteIdForBoardFragmentFragment | undefined>({
        id: identifiedTp,
        fragment: GetSiteIdForBoardFragmentFragmentDoc,
      });

      if (fragment != null) {
        return { ...fragment };
      }

      try {
        const { data } = await apolloClient.query<GetSiteIdForBoardQuery>({
          query: GetSiteIdForBoardDocument,
          variables: {
            boardId: originBoardId.value,
          },
        });

        if (data.GetSQDCBoard == null) {
          return undefined;
        }

        return {
          ...data.GetSQDCBoard,
        };
      } catch {
        return undefined;
      }
    },
    undefined,
    {
      evaluating: boardLoading,
    },
  );

  const boardLoadedNextTick = asyncComputed(async () => {
    await nextTick();
    return boardLoading.value;
  });

  return {
    board,
    siteId: computed(() => board.value?.siteId),
    boardLoading: computed(() => boardLoadedNextTick.value),
  };
}

export function useProvidedBoardContext() {
  const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));

  return useSpecifiedBoardContext(computed(() => boardId.value ?? undefined));
}

export function useGetBoardName(boardId: ComputedRef<number | undefined>) {
  const { result } = useSQDCBoardGetNameQuery(
    computed(() => ({ boardId: boardId.value! })),
    computed(() => ({
      fetchPolicy: "cache-first",
      enabled: boardId.value != null && getBoardNameFromCache(boardId.value) == null,
      errorPolicy: "ignore",
    })),
  );
  return computed(() => {
    if (boardId.value == null) {
      return undefined;
    }

    const name = getBoardNameFromCache(boardId.value);

    if (name != null) {
      return name;
    }
    return result.value?.GetSQDCBoard?.name;
  });
}

export function useGetBoardInfo(boardId: ComputedRef<number | undefined>) {
  const { result } = useSQDCBoardsForHierarchyQuery({
    fetchPolicy: "cache-first",
    errorPolicy: "ignore",
  });
  const board = computed(() =>
    result.value?.SQDCBoards.find((candidate) => candidate.id === boardId.value),
  );
  const boardName = computed(() => board.value?.name);
  const hasBoardAccess = computed(() => {
    if (board.value == null) {
      return false;
    }

    const allowedUsers = board.value.allowedUsersConnection;
    return (
      allowedUsers.length === 0 ||
      allowedUsers.some((connection) => connection.userId === Number(store.getters["login/userId"]))
    );
  });

  return {
    boardName,
    hasBoardAccess,
  };
}

export function getBoardNameFromCache(boardId: number) {
  const identifiedTp = apolloCache.identify({
    __typename: "SQDCBoard",
    id: boardId,
  });
  const fragment = apolloCache.readFragment<{ id?: number; name?: string } | undefined>({
    id: identifiedTp,
    fragment: gql`
      fragment BoardFragment on SQDCBoard {
        id
        name
      }
    `,
  });
  return fragment?.name;
}
